import { Box, Button, FormControl, Input, Option, Select, Typography } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import { useRole, isAdminRole } from "../Contexts/RoleContext";
import PortalTableLayout from "../components/PortalTableLayout";

import { debounce } from "lodash";
import { useDebounce } from "usehooks-ts";
import TicketDialog from "../Dialogs/TicketDialog";
import TicketUploadDialog, { UploadTicket } from "../Dialogs/TicketUploadDialog";
import { Mechanic } from "../Interfaces/Mechanic";
import { Elevator } from "../Interfaces/Elevator";

interface TicketListingProps {
	buildingId?: string;
	ticketTypesId?: string;
	recommendations?: boolean;
	outOfService?: boolean;
	sales?: boolean;
}

export default function TicketListing(props: TicketListingProps) {
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(10);
	const [dateFilter, setDateFilter] = React.useState(null as any);
	const [ticketTypeFilter, setTicketTypeFilter] = React.useState(props.ticketTypesId ? [props.ticketTypesId] : Util.ticketTypes.map((x) => x.ticket_type_id));
	const [globalFilter, setGlobalFilter] = React.useState("" as string);
	const globalFilterD = useDebounce<string>(globalFilter, 300);
	const [orderBy, setOrderBy] = React.useState(2);
	const [order, setOrder] = React.useState(true);
	const [tickets, setTickets] = React.useState([] as Array<any>);
	const [loading, setLoading] = React.useState(false);
	const [dialogId, setDialogId] = React.useState<string>();
	const [showDialog, setShowDialog] = React.useState(false);
	const [uploadDialog, setUploadDialog] = React.useState(false);
	const [mechanics, setMechanics] = React.useState<Array<Mechanic>>([]);
	const [elevators, setElevators] = React.useState<Array<Elevator>>([]);
	const [uploadTicket, setUploadTicket] = React.useState<UploadTicket>({
		ticketDate: new Date(),
		ticketTypesIds: [],
	} as any as UploadTicket);
	const { effectiveRole } = useRole();

	React.useEffect(() => {
		getTickets();
		!elevators.length && getElevators();
		!mechanics.length && getMechanics();
	}, [pageNumber, pageSize, globalFilterD, ticketTypeFilter, order, orderBy, props.buildingId]);

	React.useEffect(() => {
		setPageNumber(0);
	}, [props.buildingId]);

	const getElevators = () => {
		fetch("Building/GetAllElevators", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setElevators(x));
	};

	const getMechanics = () => {
		fetch("User/GetMechanics", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setMechanics(x));
	};

	const markSaleComplete = (ticketId: string) => {
		fetch("Ticket/CompleteSale", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ ticketsId: ticketId }),
		}).then(() => getTickets());
	};

	const getTickets = debounce(() => {
		setLoading(true);
		const ob = ["building_name", "serial_number", "ticket_number", "date_entered", "ticket_types", "is_submitted"];
		if (props.buildingId) {
			ob.splice(0, 1);
		}
		const orderFilter = [{ ColumnName: ob[orderBy], IsDescending: order }];

		let dt = Util.Formatter(dateFilter, "D");
		if (dt === "01/01/1900" || dt === "Invalid date") dt = null;

		fetch("/Ticket/GetPagedTickets", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				buildingId: props.buildingId || null,
				ticketTypeId: ticketTypeFilter,
				recommendations: props.recommendations || false,
				pageNumber: pageNumber,
				pageSize: pageSize,
				orderBy: orderFilter,
				ticketDate: dt,
				globalFilter: globalFilter,
				outOfService: props.outOfService || false,
				sales: props.sales || false,
			}),
		})
			.then((x) => x.json())
			.then((y) => {
				setTickets(y);
				setLoading(false);
			});
	}, 150);

	const rowCounts = [5, 10, 25, 50];
	const totalCount = tickets.length > 0 ? tickets[0]["total_row_count"] : 0;
	const cols = ["Building Name", "Serial Number", "Ticket Number", "Date", "Type", "Submitted?"];
	if (props.buildingId) {
		cols.splice(0, 1);
	}

	if (props.sales) {
		cols.push("Mark Complete");
	}

	const handleSort = (idx: number) => {
		setOrderBy(idx);
		setOrder(orderBy === idx ? !order : true);
	};

	return (
		<>
			<TicketUploadDialog
				open={uploadDialog}
				close={() => {
					setUploadDialog(false);
					getTickets();
				}}
				buildingId={(props.buildingId as string) || (uploadTicket?.buildingId as string)}
				mechanics={mechanics}
				elevators={elevators}
				ticket={uploadTicket}
			/>
			{showDialog && <TicketDialog ticketsId={dialogId as string} close={() => setShowDialog(false)} />}
			<PortalTableLayout
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						{props.buildingId && (
							<Typography level="title-md" sx={{ m: 0, color: "text.secondary" }}>
								Tickets
							</Typography>
						)}
						<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, ml: "auto" }}>
							{isAdminRole(effectiveRole) && props.buildingId && !props.ticketTypesId && !props.recommendations && !props.outOfService && (
								<Button
									variant="solid"
									color="success"
									startDecorator={<AddIcon />}
									onClick={() => {
										setUploadDialog(true);
										setUploadTicket({
											ticketDate: new Date(),
											ticketTypesIds: [],
										} as any as UploadTicket);
									}}>
									Add Paper Ticket
								</Button>
							)}
							<FormControl sx={{ minWidth: 180 }}>
								<Input
									name="Search"
									placeholder="Global Search"
									onChange={(event) => {
										setGlobalFilter(event.target.value);
									}}
									value={globalFilter}
								/>
							</FormControl>
							{Util.isNullOrEmpty(props.ticketTypesId) && (
								<Select
									placeholder="Ticket Types"
									value={ticketTypeFilter}
									renderValue={() => "Ticket Types"}
									multiple
									sx={{ minWidth: 140 }}
									onChange={(event, value) => {
										setTicketTypeFilter(value ?? []);
									}}>
									<>
										{Util.ticketTypes.map((x) => (
											<Option key={x.ticket_type_id} value={x.ticket_type_id}>
												{x.description}
											</Option>
										))}
									</>
								</Select>
							)}
						</Box>
					</Box>
				}
				pagination={{
					count: totalCount,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: rowCounts,
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{cols.map((x, idx) => (
								<TableCell key={x} sortDirection={orderBy === idx ? (order ? "desc" : "asc") : false}>
									<TableSortLabel active={orderBy === idx} direction={orderBy === idx ? (order ? "desc" : "asc") : "asc"} onClick={() => handleSort(idx)}>
										{x}
									</TableSortLabel>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && (
							<TableRow>
								<TableCell colSpan={cols.length} align="center" sx={{ py: 3 }}>
									<CircularProgress size={32} />
								</TableCell>
							</TableRow>
						)}
						{!loading &&
							tickets.map((t) => (
								<TableRow
									key={t.tickets_id}
									hover
									sx={{ cursor: "pointer" }}
									onClick={() => {
										if (!t.is_pdf_data) {
											setShowDialog(true);
											setDialogId(t.tickets_id);
										} else {
											fetch("Ticket/GetUploadTicketData", {
												method: "Post",
												cache: "no-cache",
												headers: {
													"Content-Type": "application/json",
												},
												body: JSON.stringify(t.tickets_id),
											})
												.then((x) => x.json())
												.then((x) => {
													setUploadDialog(true);
													setUploadTicket({
														elevatorId: x.elevators_id,
														buildingId: x.buildings_id,
														ticketDate: Util.ParseDate(x.date_entered),
														submitted: x.submitted,
														ticketTypesIds: (x.ticket_type ?? "").split(","),
														ticketsId: x.tickets_id,
														mechanicId: x.user_id,
														ticketNumber: x.ticket_number,
														workPerformed: x.work_performed,
														buildingName: x.building_name,
													} as UploadTicket);
												});
										}
									}}>
									{!props.buildingId && <TableCell>{t.building_name}</TableCell>}
									<TableCell>{t.serial_number}</TableCell>
									<TableCell>{t.ticket_number}</TableCell>
									<TableCell>{Util.Formatter(t.date_entered, "D")}</TableCell>
									<TableCell>{t.ticket_types}</TableCell>
									<TableCell>{Util.Formatter(t.is_submitted, "L")}</TableCell>
									{props.sales && (
										<TableCell>
											<Button
												onClick={(event) => {
													markSaleComplete(t.tickets_id);
													event?.preventDefault();
													event?.stopPropagation();
												}}>
												Mark Sale Complete
											</Button>
										</TableCell>
									)}
								</TableRow>
							))}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
